import { createContext, useContext } from "react";

/**
 * Portal language. Order of precedence: `?lang=` (testing) → stored choice (`iga.locale`) → tenant default
 * (when the API exposes it to the signed-in user, see App.tsx) → browser language. Only `en` and `da` ship.
 */
export type PortalLocale = "en" | "da";
const STORAGE_KEY = "iga.locale";

export function normaliseLocale(v: string | null | undefined): PortalLocale | null {
  const s = (v ?? "").toLowerCase();
  if (s.startsWith("da")) return "da";
  if (s.startsWith("en")) return "en";
  return null;
}

export function storedLocale(): PortalLocale | null {
  try { return normaliseLocale(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

export function storeLocale(l: PortalLocale | null): void {
  try { if (l) localStorage.setItem(STORAGE_KEY, l); else localStorage.removeItem(STORAGE_KEY); } catch { /* storage blocked */ }
}

export function detectLocale(): PortalLocale {
  const q = typeof window !== "undefined" ? normaliseLocale(new URLSearchParams(window.location.search).get("lang")) : null;
  if (q) { storeLocale(q); return q; }
  return storedLocale() ?? normaliseLocale(typeof navigator !== "undefined" ? navigator.language : "en") ?? "en";
}

export const LocaleSettingContext = createContext<{ locale: PortalLocale; setLocale: (l: PortalLocale) => void }>({ locale: "en", setLocale: () => undefined });
export const useLocaleSetting = () => useContext(LocaleSettingContext);
